import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Plus, Edit2, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import ErrorModal from '@/components/ErrorModal';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [newUser, setNewUser] = useState({ user_id: '', password: '', role: 'user' });
  const [editUser, setEditUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  // Error modal state
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: 'Error',
    message: ''
  });

  const load = () => {
    api.get('/users').then(r => setUsers(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleCreate = async () => {
    if (!newUser.user_id || !newUser.password) { 
      setErrorModal({
        open: true,
        title: 'Campos requeridos faltantes',
        message: 'Por favor, completa los campos: ID de usuario y Contraseña.'
      });
      return; 
    }
    try {
      await api.post('/users', newUser);
      toast.success('Usuario creado');
      setShowCreate(false);
      setNewUser({ user_id: '', password: '', role: 'user' });
      load();
    } catch (err) { 
      const errorMessage = err.response?.data?.detail || 'Error al crear usuario';
      setErrorModal({
        open: true,
        title: 'Error al crear usuario',
        message: errorMessage
      });
    }
  };

  const handleToggleActive = async (u) => {
    await api.put(`/users/${u.id}`, { active: !u.active });
    toast.success('Estado actualizado');
    load();
  };

  const handleEdit = (u) => {
    setEditUser({ ...u });
    setNewPassword('');
    setShowEdit(u.id);
  };

  const handleSaveEdit = async () => {
    try {
      // Actualizar datos básicos del usuario
      await api.put(`/users/${editUser.id}`, { 
        active: editUser.active,
        role: editUser.role 
      });
      
      // Si se proporcionó una nueva contraseña, actualizarla
      if (newPassword.trim()) {
        await api.post(`/users/${editUser.id}/reset-password`, { 
          new_password: newPassword 
        });
        toast.success('Usuario y contraseña actualizados');
      } else {
        toast.success('Usuario actualizado');
      }
      
      setShowEdit(null);
      setEditUser(null);
      setNewPassword('');
      load();
    } catch (err) { 
      const errorMessage = err.response?.data?.detail || 'Error al actualizar usuario';
      setErrorModal({
        open: true,
        title: 'Error al actualizar usuario',
        message: errorMessage
      });
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Usuario eliminado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Filtrar usuarios por búsqueda
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(u => 
      u.user_id.toLowerCase().includes(q) ||
      u.role.toLowerCase().includes(q) ||
      (u.id && u.id.toLowerCase().includes(q))
    );
  }, [users, searchQuery]);

  // Paginación
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + usersPerPage);

  // Generar números de página para mostrar
  const pageNumbers = useMemo(() => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage, '...', totalPages);
      }
    }
    return pages;
  }, [currentPage, totalPages]);

  return (
    <div className="space-y-4 animate-fade-in p-4 lg:p-6" data-testid="admin-users-page">
      {/* Título */}
      <h2 className="text-3xl font-black gold-text text-center lg:text-left" style={{ fontFamily: 'Montserrat' }}>
        Listado de Usuarios
      </h2>

      {/* Barra de búsqueda */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#B89558]" size={20} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
          placeholder="Buscar por nombre, correo o ID..."
          className="w-full bg-[#0A1220]/80 border-2 border-[#926F39]/50 rounded-lg pl-12 pr-4 py-3 text-white placeholder-white/40 focus:border-[#F4E39E] focus:ring-2 focus:ring-[#926F39]/30 outline-none transition-all"
        />
      </div>

      {loading ? (
        <div className="text-center text-white/50 py-10">Cargando...</div>
      ) : (
        <>
          {/* Cards individuales para Desktop y Mobile */}
          <div className="space-y-4">
            {/* Header de columnas */}
            <div className="grid grid-cols-4 gap-2 lg:gap-4 px-4 lg:px-6 py-3 glass-card-subtle text-xs lg:text-sm">
              <div className="text-[#F4E39E] font-bold">ID Usuario</div>
              <div className="text-[#F4E39E] font-bold text-center">Rango</div>
              <div className="text-[#F4E39E] font-bold text-center">Estado</div>
              <div className="text-[#F4E39E] font-bold text-center">Acciones</div>
            </div>

            {/* Cards de usuarios */}
            {paginatedUsers.map((u, idx) => (
              <div 
                key={u.id} 
                className="glass-card p-4 lg:p-6 hover:border-[#F4E39E]/50 transition-colors"
                data-testid={`user-row-${u.user_id}`}
              >
                {/* Desktop Layout */}
                <div className="hidden lg:grid lg:grid-cols-4 gap-4 items-center">
                  <div className="text-white/90 font-medium">
                    {u.user_id}
                  </div>
                  <div className="text-white/70">
                    Usuario {u.role === 'admin' ? 'Admin' : 'VIP'}
                  </div>
                  <div>
                    <span className={`font-medium ${u.active ? 'text-green-400' : 'text-red-400'}`}>
                      {u.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <button 
                      onClick={() => handleEdit(u)}
                      className="gold-btn px-4 py-2 rounded-lg text-xs font-bold"
                    >
                      Ver Perfil
                    </button>
                    <button 
                      onClick={() => handleEdit(u)}
                      className="p-2 rounded-lg hover:bg-white/10 text-[#F4E39E] hover:text-white transition-colors"
                      title="Editar"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button 
                      onClick={() => handleDelete(u.id)}
                      className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Mobile Layout - 4 columnas horizontales */}
                <div className="lg:hidden grid grid-cols-4 gap-2 items-center text-xs">
                  <div className="text-white/90">{u.user_id}</div>
                  <div className="text-white/70 text-center">
                    {u.role === 'admin' ? 'Admin' : 'VIP'}
                  </div>
                  <div className="text-center">
                    <span className={`text-xs font-medium ${u.active ? 'text-green-400' : 'text-red-400'}`}>
                      {u.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-1">
                    <button 
                      onClick={() => handleEdit(u)}
                      className="p-1.5 rounded-lg hover:bg-white/10 text-[#F4E39E]"
                      title="Editar"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button 
                      onClick={() => handleDelete(u.id)}
                      className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400"
                      title="Eliminar"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 text-[#F4E39E] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              {pageNumbers.map((num, idx) => (
                <button
                  key={idx}
                  onClick={() => typeof num === 'number' && setCurrentPage(num)}
                  disabled={num === '...'}
                  className={`px-3 py-1 rounded-lg font-medium transition-colors ${
                    num === currentPage
                      ? 'bg-[#926F39] text-white'
                      : num === '...'
                      ? 'text-white/50 cursor-default'
                      : 'text-[#F4E39E] hover:bg-white/10'
                  }`}
                >
                  {num}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 text-[#F4E39E] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}

          {/* Botón Crear Nuevo Usuario */}
          <button 
            onClick={() => setShowCreate(true)} 
            className="gold-btn w-full py-3 rounded-lg text-base font-bold flex items-center justify-center gap-2" 
            data-testid="create-user-button"
          >
            Crear Nuevo Usuario
            <Plus size={20} />
          </button>

          {/* Pie de texto */}
          <p className="text-center text-white/50 text-sm">Gestiona usuarios</p>
        </>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#111A30] border-[#926F39]/30 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="gold-text" style={{ fontFamily: 'Montserrat' }}>Crear Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-white/60 text-sm">ID de usuario</label>
              <input 
                value={newUser.user_id} 
                onChange={e => setNewUser({ ...newUser, user_id: e.target.value })} 
                data-testid="new-user-id-input"
                className="w-full bg-[#0A0F1C] border border-[#926F39]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" 
                placeholder="ID unico" 
              />
              <p className="text-white/30 text-xs mt-1">El ID debe ser unico</p>
            </div>
            <div>
              <label className="text-white/60 text-sm">Contrasena</label>
              <input 
                type="password" 
                value={newUser.password} 
                onChange={e => setNewUser({ ...newUser, password: e.target.value })} 
                data-testid="new-user-password-input"
                className="w-full bg-[#0A0F1C] border border-[#926F39]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" 
              />
            </div>
            <button 
              onClick={handleCreate} 
              className="gold-btn w-full py-3 rounded-lg font-bold" 
              data-testid="save-user-button"
            >
              Guardar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!showEdit} onOpenChange={() => { setShowEdit(null); setEditUser(null); setNewPassword(''); }}>
        <DialogContent className="bg-[#111A30] border-[#926F39]/30 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="gold-text" style={{ fontFamily: 'Montserrat' }}>Ver Perfil</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <div>
                <label className="text-white/60 text-sm">ID de usuario</label>
                <div className="w-full bg-[#0A0F1C]/50 border border-[#926F39]/40 rounded-lg px-4 py-3 text-white/70 mt-1">
                  {editUser.user_id}
                </div>
              </div>
              <div>
                <label className="text-white/60 text-sm">Rol</label>
                <select
                  value={editUser.role}
                  onChange={e => setEditUser({ ...editUser, role: e.target.value })}
                  className="w-full bg-[#0A0F1C] border border-[#926F39]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none"
                >
                  <option value="user">Usuario VIP</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="text-white/60 text-sm">Nueva Contraseña</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Dejar en blanco para no cambiar"
                  className="w-full bg-[#0A0F1C] border border-[#926F39]/40 rounded-lg px-4 py-3 text-white placeholder-white/30 mt-1 focus:border-[#F4E39E] outline-none"
                />
                <p className="text-white/30 text-xs mt-1">Solo ingresa una contraseña si deseas cambiarla</p>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-white/60 text-sm">Estado</label>
                <button
                  onClick={() => setEditUser({ ...editUser, active: !editUser.active })}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    editUser.active 
                      ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                      : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                  }`}
                >
                  {editUser.active ? 'Activo' : 'Inactivo'}
                </button>
              </div>
              <button 
                onClick={handleSaveEdit} 
                className="gold-btn w-full py-3 rounded-lg font-bold"
              >
                Guardar Cambios
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <ErrorModal
        open={errorModal.open}
        onClose={() => setErrorModal({ ...errorModal, open: false })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
}
